import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, Play, ExternalLink } from "lucide-react";
import { toast } from "sonner";

interface VideoItem {
  title: string;
  description: string;
  url: string;
  thumbnail?: string;
}

const VideosUsadosPage = () => {
  const [copied, setCopied] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<VideoItem | null>(null);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    toast.success("Link copiado para a área de transferência!");
    setTimeout(() => setCopied(null), 2000);
  };

  const handlePlayVideo = (video: VideoItem) => {
    setSelectedVideo(video);
    toast.success(`Reproduzindo: ${video.title}`);
  };

  const sonicSprountVideos: VideoItem[] = [
    {
      title: "CANAL DE GATOS NO RUMBLE 1",
      description: "Conheça o canal de gatos no Rumble",
      url: "https://www.youtube.com/watch?v=sj9vd83Q5V8",
      thumbnail: "https://i.ytimg.com/vi/sj9vd83Q5V8/hqdefault.jpg"
    },
    {
      title: "Criando canal musical 1",
      description: "Aprenda a criar um canal musical no YouTube - Parte 1",
      url: "https://www.youtube.com/watch?v=sD2PgeQiPJ8",
      thumbnail: "https://i.ytimg.com/vi/sD2PgeQiPJ8/hqdefault.jpg"
    },
    {
      title: "Criando canal musical 2",
      description: "Aprenda a criar um canal musical no YouTube - Parte 2",
      url: "https://www.youtube.com/watch?v=GWG_WLyRL0g",
      thumbnail: "https://i.ytimg.com/vi/GWG_WLyRL0g/hqdefault.jpg"
    },
    {
      title: "Criando canal musical",
      description: "Dicas gerais para criar um canal musical",
      url: "https://www.youtube.com/watch?v=UGdGoSbg9d8",
      thumbnail: "https://i.ytimg.com/vi/UGdGoSbg9d8/hqdefault.jpg"
    }
  ];

  const bannerJazzVideos: VideoItem[] = [
    {
      title: "Banner para YouTube de música de jazz com estilo monolinha em preto e branco 4",
      description: "Tutorial para criar banners para canal de música de jazz - Parte 4",
      url: "https://www.youtube.com/watch?v=pIxgVSURhz0",
      thumbnail: "https://i.ytimg.com/vi/pIxgVSURhz0/hqdefault.jpg"
    },
    {
      title: "Banner para YouTube de música de jazz com estilo monolinha em preto e branco 3",
      description: "Tutorial para criar banners para canal de música de jazz - Parte 3",
      url: "https://www.youtube.com/watch?v=8Qu9WlbZojE",
      thumbnail: "https://i.ytimg.com/vi/8Qu9WlbZojE/hqdefault.jpg"
    },
    {
      title: "Banner para YouTube de música de jazz com estilo monolinha em preto e branco 1",
      description: "Tutorial para criar banners para canal de música de jazz - Parte 1",
      url: "https://www.youtube.com/watch?v=su4hD81B788",
      thumbnail: "https://i.ytimg.com/vi/su4hD81B788/hqdefault.jpg"
    }
  ];

  const canalVideos: VideoItem[] = [
    {
      title: "Sonic Sprount Canal",
      description: "Introdução ao canal Sonic Sprount",
      url: "https://www.youtube.com/watch?v=H7SJU4FhJE8",
      thumbnail: "https://i.ytimg.com/vi/H7SJU4FhJE8/hqdefault.jpg"
    },
    {
      title: "Se inscreve no canal Sonic Sprount",
      description: "Visite e inscreva-se no canal Sonic Sprount",
      url: "https://studio.youtube.com/channel/UChR7_LeRtstmwME4uLBjOig",
      thumbnail: "https://placehold.co/600x400/e9d8fd/6b46c1?text=Sonic+Sprount+Canal"
    }
  ];

  const renderVideoModal = () => {
    if (!selectedVideo) return null;
    
    const videoId = selectedVideo.url.includes("youtu.be/") 
      ? selectedVideo.url.split("youtu.be/")[1]
      : selectedVideo.url.includes("youtube.com/watch?v=")
        ? selectedVideo.url.split("v=")[1].split("&")[0]
        : selectedVideo.url.includes("youtube.com/live/")
          ? selectedVideo.url.split("live/")[1].split("?")[0]
          : "";

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-card rounded-lg overflow-hidden w-full max-w-4xl">
          <div className="p-4 flex justify-between items-center border-b">
            <h3 className="text-lg font-medium">{selectedVideo.title}</h3>
            <Button variant="ghost" size="sm" onClick={() => setSelectedVideo(null)}>
              ✕
            </Button>
          </div>
          <div className="aspect-video w-full">
            {videoId ? (
              <iframe 
                width="100%" 
                height="100%" 
                src={`https://www.youtube.com/embed/${videoId}`}
                title={selectedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-muted">
                <p>Este vídeo não pode ser incorporado. <a href={selectedVideo.url} target="_blank" rel="noopener noreferrer" className="text-primary">Abrir link externo</a></p>
              </div>
            )}
          </div>
          <div className="p-4">
            <p className="text-muted-foreground">{selectedVideo.description}</p>
          </div>
          <div className="p-4 border-t flex justify-end space-x-2">
            <Button 
              variant="outline" 
              onClick={() => handleCopy(selectedVideo.url)}
            >
              <Copy size={16} className="mr-2" /> Copiar Link
            </Button>
            <Button 
              asChild 
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              <a href={selectedVideo.url} target="_blank" rel="noopener noreferrer">
                <ExternalLink size={16} className="mr-2" /> Abrir no YouTube
              </a>
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent py-2">
            Os Melhores Vídeos Usados
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Coleção de vídeos utilizados para tutoriais, referências e inspiração na criação de conteúdo para canais do YouTube.
          </p>
        </div>

        <Tabs defaultValue="sonicSprount" className="w-full mb-12">
          <div className="flex justify-center mb-8">
            <TabsList>
              <TabsTrigger value="sonicSprount">Criando Canal Musical</TabsTrigger>
              <TabsTrigger value="bannerJazz">Banners para Jazz</TabsTrigger>
              <TabsTrigger value="canalVideos">Canal Sonic Sprount</TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="sonicSprount" className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {sonicSprountVideos.map((video, index) => (
                <Card key={index} className="overflow-hidden flex flex-col h-full">
                  <div className="aspect-video relative cursor-pointer" onClick={() => handlePlayVideo(video)}>
                    <img 
                      src={video.thumbnail} 
                      alt={video.title} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = `https://placehold.co/600x400/e9d8fd/6b46c1?text=${encodeURIComponent(video.title)}`;
                      }}
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <Button className="bg-primary/90 hover:bg-primary text-primary-foreground">
                        <Play className="mr-2 h-4 w-4" /> Reproduzir
                      </Button>
                    </div>
                  </div>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{video.title}</CardTitle>
                    <CardDescription>{video.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="pb-2 pt-0 flex-grow">
                    <p className="text-sm text-muted-foreground truncate">{video.url}</p>
                  </CardContent>
                  <CardFooter className="pt-0">
                    <div className="flex space-x-2 w-full">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => handleCopy(video.url)}
                      >
                        <Copy size={16} className="mr-2" /> Copiar Link
                      </Button>
                      <Button 
                        className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                        onClick={() => handlePlayVideo(video)}
                      >
                        <Play size={16} className="mr-2" /> Assistir
                      </Button>
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="bannerJazz" className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {bannerJazzVideos.map((video, index) => (
                <Card key={index} className="overflow-hidden flex flex-col h-full">
                  <div className="aspect-video relative cursor-pointer" onClick={() => handlePlayVideo(video)}>
                    <img 
                      src={video.thumbnail} 
                      alt={video.title} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = `https://placehold.co/600x400/e9d8fd/6b46c1?text=${encodeURIComponent(video.title)}`;
                      }}
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <Button className="bg-primary/90 hover:bg-primary text-primary-foreground">
                        <Play className="mr-2 h-4 w-4" /> Reproduzir
                      </Button>
                    </div>
                  </div>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{video.title}</CardTitle>
                    <CardDescription>{video.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="pb-2 pt-0 flex-grow">
                    <p className="text-sm text-muted-foreground truncate">{video.url}</p>
                  </CardContent>
                  <CardFooter className="pt-0">
                    <div className="flex space-x-2 w-full">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => handleCopy(video.url)}
                      >
                        <Copy size={16} className="mr-2" /> Copiar Link
                      </Button>
                      <Button 
                        className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                        onClick={() => handlePlayVideo(video)}
                      >
                        <Play size={16} className="mr-2" /> Assistir
                      </Button>
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="canalVideos" className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {canalVideos.map((video, index) => (
                <Card key={index} className="overflow-hidden flex flex-col h-full">
                  <div className="aspect-video relative cursor-pointer" onClick={() => handlePlayVideo(video)}>
                    <img 
                      src={video.thumbnail} 
                      alt={video.title} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = `https://placehold.co/600x400/e9d8fd/6b46c1?text=${encodeURIComponent(video.title)}`;
                      }}
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <Button className="bg-primary/90 hover:bg-primary text-primary-foreground">
                        <Play className="mr-2 h-4 w-4" /> Reproduzir
                      </Button>
                    </div>
                  </div>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{video.title}</CardTitle>
                    <CardDescription>{video.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="pb-2 pt-0 flex-grow">
                    <p className="text-sm text-muted-foreground truncate">{video.url}</p>
                  </CardContent>
                  <CardFooter className="pt-0">
                    <div className="flex space-x-2 w-full">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => handleCopy(video.url)}
                      >
                        <Copy size={16} className="mr-2" /> Copiar Link
                      </Button>
                      {video.url.includes("youtube.com/watch") ? (
                        <Button 
                          className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                          onClick={() => handlePlayVideo(video)}
                        >
                          <Play size={16} className="mr-2" /> Assistir
                        </Button>
                      ) : (
                        <Button 
                          asChild
                          className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                        >
                          <a href={video.url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink size={16} className="mr-2" /> Visitar
                          </a>
                        </Button>
                      )}
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <div className="bg-muted p-6 rounded-lg mt-12">
          <h2 className="text-2xl font-semibold mb-4">Como usar estes vídeos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium mb-2">Para Criadores de Conteúdo:</h3>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                <li>Use estes vídeos como referência para criar seu próprio canal musical</li>
                <li>Aprenda técnicas de design para banners de jazz em estilo monolinha</li>
                <li>Inspire-se nas dicas para crescer seu canal no YouTube</li>
                <li>Compartilhe com outros criadores que estão começando</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-medium mb-2">Para Fãs e Seguidores:</h3>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                <li>Inscreva-se no canal Sonic Sprount para receber mais conteúdo</li>
                <li>Compartilhe os vídeos que mais gostou com amigos</li>
                <li>Deixe comentários e sugestões para futuros vídeos</li>
                <li>Ative as notificações para não perder nenhuma novidade</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="text-center mt-12">
          <h2 className="text-2xl font-semibold mb-4">Inscreva-se no Canal</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
            Acompanhe o canal Sonic Sprount para receber as melhores dicas sobre criação de conteúdo musical e design para o YouTube.
          </p>
          <Button 
            asChild 
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <a 
              href="https://studio.youtube.com/channel/UChR7_LeRtstmwME4uLBjOig" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <ExternalLink className="mr-2 h-5 w-5" /> Se inscreve no canal Sonic Sprount
            </a>
          </Button>
        </div>
      </main>
      <Footer />
      {renderVideoModal()}
    </div>
  );
};

export default VideosUsadosPage;